import { useCallback } from "react";
import { io } from "socket.io-client";
import appLogger from "../../../infra/logger/appLogger";
import { authProvider } from "../../diProviders";
import { useTranslation } from "../../localization/hooks";
import { getRateLimitErrorMessage } from "../../miscellaneous/getRateLimitErrorMessage";
import { ModelId, SampleRate } from "../voices";
import { SocketEmitEvent, SocketEvent } from "./types";
/* istanbul ignore file */
const socketLogger = appLogger.withTag("TtsSocket");
const useSocket = ({ socketUrl, playChatMessage, setMessageId, messageId, handleError }) => {
    const { t } = useTranslation();
    const RATE_LIMIT_ERROR_MESSAGES = getRateLimitErrorMessage(t);
    let socket = null;
    const initiateSocketConnection = (token) => {
        if (!socket) {
            const socketServerURL = socketUrl;
            socket = io(`${socketServerURL}/?token=${token}`, {
                transports: ["websocket", "polling"],
                withCredentials: true,
            });
            socketLogger.info("Connected to Socket.io server");
        }
        return socket;
    };
    const disconnectSocket = () => {
        if (socket) {
            socket.disconnect();
            socket = null;
            socketLogger.info("Disconnected from Socket.io server");
        }
    };
    const connectSocket = useCallback(async () => {
        const token = await authProvider.getToken();
        socket = initiateSocketConnection(token);
        socket.on(SocketEvent.AUDIO_DATA, (response) => {
            playChatMessage(response);
        });
        /* istanbul ignore next */
        socket.on(SocketEvent.ERROR, (err) => {
            socketLogger.error("Socket io error", err);
            handleError(SocketEvent.ERROR, err);
        });
        socket.on(SocketEvent.SOCKET_ERROR, (err) => {
            socketLogger.error(`connect_error due to ${err.message}`);
            handleError(SocketEvent.ERROR, "There was an error generating audio, Please try again.");
        });
        socket.on(SocketEvent.RATE_LIMIT_REACHED, () => {
            handleError(SocketEvent.RATE_LIMIT_REACHED, RATE_LIMIT_ERROR_MESSAGES.RATE_LIMIT_BREACHED_PLAY_MESSAGE_AUDIO);
        });
        return socket;
    }, []);
    const requestAudioPlay = useCallback(async (requestedMessageId, voiceId, modelId = ModelId.SONIC_3, sampleRate = SampleRate.SAMPLE_RATE_48000) => {
        if (!voiceId || !requestedMessageId || messageId === requestedMessageId) {
            return;
        }
        setMessageId(requestedMessageId);
        if (!socket || !socket.connected) {
            socket = await connectSocket();
        }
        socket.emit(SocketEmitEvent.AUDIO_BY_MESSAGE_ID, requestedMessageId, voiceId, modelId, sampleRate);
    }, []);
    return {
        connectSocket,
        requestAudioPlay,
        disconnectSocket,
    };
};
export default useSocket;
